import { Money } from '../../money/model/money.types';
import { Environment } from '../../environment/model/environment.types';
import { convertDateFromDateArray } from '@starjack/utils';
import {
    BackendType,
    FrontendType,
    ObjectType,
    StarjackDate,
} from '@starjack/utils';
import { ChipProps } from '@mui/material';

export enum VoucherStatus {
    NEW = 'NEW',
    REDEEMED = 'REDEEMED',
}

export type VoucherTransaction<T extends ObjectType = FrontendType> = {
    id: string;
    timestamp: StarjackDate<T>;
    amount: Money;
    reference: 'Payment' | 'Redemption';
    userId: string;
    orderId: string | null;
    environment: Environment;
    externalPayment: boolean;
};

const convertVoucherTransactionToFrontend = (
    transaction: VoucherTransaction<BackendType>
): VoucherTransaction => ({
    ...transaction,
    timestamp: convertDateFromDateArray(transaction.timestamp),
});

export type Voucher<T extends ObjectType = FrontendType> = {
    id: string;
    orderId: string | null;
    createdBy: string;
    environment: Environment;
    creationDate: StarjackDate<T>;
    validUntil: StarjackDate<T> | null;
    integrationRef: string | null;
    initialAmount: Money;
    sender: string;
    receiver: string;
    title: string;
    code: string | null;
    status: VoucherStatus;
    amount: Money;
    redeemableAmount: Money;
    transactions: VoucherTransaction<T>[];
    reservations: unknown[];
};

export const convertVoucherToFrontend = (
    responseObject: Voucher<BackendType>
): Voucher => ({
    ...responseObject,
    creationDate: convertDateFromDateArray(responseObject.creationDate),
    validUntil: responseObject.validUntil
        ? convertDateFromDateArray(responseObject.validUntil)
        : null,
    transactions:
        responseObject.transactions?.map(convertVoucherTransactionToFrontend) ||
        [],
});

export const getVoucherStatusColor = (
    status: Voucher['status']
): ChipProps['color'] => {
    switch (status) {
        case 'REDEEMED':
            return 'success';
        case 'NEW':
            return 'primary';
        default:
            return 'info';
    }
};

// Helper functions to extract voucher details
export const getVoucherPurchaseTransaction = (
    voucher: Voucher
): VoucherTransaction | null => {
    return voucher.transactions.find((t) => t.reference === 'Payment') || null;
};

export const getVoucherRedemptionTransaction = (
    voucher: Voucher
): VoucherTransaction | null => {
    return (
        voucher.transactions.find((t) => t.reference === 'Redemption') || null
    );
};

export const getVoucherPurchasedBy = (voucher: Voucher): string => {
    const purchaseTransaction = getVoucherPurchaseTransaction(voucher);
    return purchaseTransaction?.userId || voucher.createdBy;
};

export const getVoucherRedeemedBy = (voucher: Voucher): string | null => {
    const redemptionTransaction = getVoucherRedemptionTransaction(voucher);
    return redemptionTransaction?.userId || null;
};

export const getVoucherPurchasedOn = (voucher: Voucher): Date | null => {
    const purchaseTransaction = getVoucherPurchaseTransaction(voucher);
    return purchaseTransaction?.timestamp || voucher.creationDate;
};

export const getVoucherRedeemedOn = (voucher: Voucher): Date | null => {
    const redemptionTransaction = getVoucherRedemptionTransaction(voucher);
    return redemptionTransaction?.timestamp || null;
};

export const getVoucherSalesInterface = (voucher: Voucher): string => {
    if (voucher.integrationRef) {
        // Mountain railway voucher - would need lift operator name
        return `Mountain railway voucher (${voucher.integrationRef})`;
    }
    return 'starjack.Portal';
};
